import type { AppState, NormalizedZoomValue } from '@xuwo/drawing/types';
import type { AppProps } from '@xuwo/drawing/types.hostApi';

import type { ExcalidrawElement, FontFamilyValues } from '../element/types';
import { COLOR_PALETTE } from './colors';

export { DEFAULT_STICKY_NOTE_BG } from './colors';

// distance when creating text before it's considered `autoResize: false`
// we're using higher threshold so that clicks that end up being drags
// don't unintentionally create text elements that are wrapped to a few chars
// (happens a lot with fast clicks with the text tool)
export const TEXT_AUTOWRAP_THRESHOLD = 36; // px
// room left at each side of the visible canvas when a text stops growing at
// its width, or is brought into view once it wraps
export const TEXT_VIEWPORT_PADDING = 20; // px
// the widest a typed or pasted text gets before it wraps (in scene units, so
// at any zoom), however wide the view
export const TEXT_MAX_WRAP_WIDTH = 800; // px
export const DRAGGING_THRESHOLD = 10; // px
export const MINIMUM_ARROW_SIZE = 20; // px
export const LINE_CONFIRM_THRESHOLD = 8; // px
export const ELEMENT_SHIFT_TRANSLATE_AMOUNT = 5;
export const ELEMENT_TRANSLATE_AMOUNT = 1;
export const TEXT_TO_CENTER_SNAP_THRESHOLD = 30;
export const SHIFT_LOCKING_ANGLE = Math.PI / 12;
export const DEFAULT_LASER_COLOR = 'red';
export const CURSOR_TYPE = {
  TEXT: 'text',
  CROSSHAIR: 'crosshair',
  GRABBING: 'grabbing',
  GRAB: 'grab',
  POINTER: 'pointer',
  MOVE: 'move',
  AUTO: '',
};
export const POINTER_BUTTON = {
  MAIN: 0,
  WHEEL: 1,
  SECONDARY: 2,
  TOUCH: -1,
  ERASER: 5,
} as const;

export const POINTER_EVENTS = {
  enabled: 'all',
  disabled: 'none',
  // asserted as any so it can be freely assigned to React Element
  // "pointerEnvets" CSS prop
  inheritFromUI: 'var(--ui-pointerEvents)' as any,
} as const;

export enum EVENT {
  COPY = 'copy',
  PASTE = 'paste',
  CUT = 'cut',
  KEYDOWN = 'keydown',
  KEYUP = 'keyup',
  MOUSE_MOVE = 'mousemove',
  RESIZE = 'resize',
  UNLOAD = 'unload',
  FOCUS = 'focus',
  BLUR = 'blur',
  DRAG_OVER = 'dragover',
  DROP = 'drop',
  GESTURE_END = 'gestureend',
  BEFORE_UNLOAD = 'beforeunload',
  GESTURE_START = 'gesturestart',
  GESTURE_CHANGE = 'gesturechange',
  POINTER_MOVE = 'pointermove',
  POINTER_DOWN = 'pointerdown',
  POINTER_UP = 'pointerup',
  POINTER_CANCEL = 'pointercancel',
  STATE_CHANGE = 'statechange',
  WHEEL = 'wheel',
  TOUCH_START = 'touchstart',
  TOUCH_END = 'touchend',
  HASHCHANGE = 'hashchange',
  VISIBILITY_CHANGE = 'visibilitychange',
  SCROLL = 'scroll',
  // custom events
  EXCALIDRAW_LINK = 'excalidraw-link',
  MENU_ITEM_SELECT = 'menu.itemSelect',
  MESSAGE = 'message',
  FULLSCREENCHANGE = 'fullscreenchange',
}

export const ENV = {
  TEST: 'test',
  DEVELOPMENT: 'development',
  PRODUCTION: 'production',
};

export const CLASSES = {
  SIDEBAR: 'sidebar',
  SHAPE_ACTIONS_MENU: 'App-menu__left',
  ZOOM_ACTIONS: 'zoom-actions',
  SEARCH_MENU_INPUT_WRAPPER: 'layer-ui__search-inputWrapper',
  CONVERT_ELEMENT_TYPE_POPUP: 'ConvertElementTypePopup',
  SHAPE_ACTIONS_THEME_SCOPE: 'shape-actions-theme-scope',
  FRAME_NAME: 'frame-name',
  DROPDOWN_MENU_EVENT_WRAPPER: 'dropdown-menu-event-wrapper',
  // XuWo: the box an embed's host content is rendered in
  EMBEDDABLE_CONTAINER: 'excalidraw__embeddable-container',
};

export const FONT_SIZES = {
  sm: 16,
  md: 20,
  lg: 28,
  xl: 36,
} as const;

/**
 * Font family ids stored on text elements (`fontFamily`). The numbers are
 * upstream's, so boards pasted from other Excalidraw copies keep their
 * meaning. XuWo offers the host's sans, serif and monospace stacks plus the
 * bundled hand-drawn Excalifont. Other upstream families (Virgil, Nunito, …)
 * are not shipped and render with the nearest of these. Serif is a XuWo id.
 */
export const FONT_FAMILY = {
  Virgil: 1,
  Sans: 2,
  Mono: 3,
  // 4 was historically used upstream for Assistant / custom fonts
  Excalifont: 5,
  Nunito: 6,
  'Lilita One': 7,
  'Comic Shanns': 8,
  'Liberation Sans': 9,
  Assistant: 10,
  Serif: 11,
};

/** The families the board offers; the rest are read-only legacy ids. */
export const FONT_FAMILY_CHOICES = [
  FONT_FAMILY.Sans,
  FONT_FAMILY.Serif,
  FONT_FAMILY.Excalifont,
  FONT_FAMILY.Mono,
] as const;

/** CSS family name of the bundled hand-drawn face (OFL, see public/fonts). */
export const HAND_DRAWN_FONT_NAME = 'Excalifont';

export const THEME = {
  LIGHT: 'light',
  DARK: 'dark',
} as const;

export const DARK_THEME_FILTER = 'invert(93%) hue-rotate(180deg)';

export const FRAME_STYLE = {
  strokeColor: '#bbb' as ExcalidrawElement['strokeColor'],
  strokeWidth: 2 as ExcalidrawElement['strokeWidth'],
  strokeStyle: 'solid' as ExcalidrawElement['strokeStyle'],
  fillStyle: 'solid' as ExcalidrawElement['fillStyle'],
  roughness: 0 as ExcalidrawElement['roughness'],
  roundness: null as ExcalidrawElement['roundness'],
  backgroundColor: 'transparent' as ExcalidrawElement['backgroundColor'],
  radius: 8,
  nameOffsetY: 3,
  nameColorLightTheme: '#999999',
  nameColorDarkTheme: '#7a7a7a',
  nameFontSize: 14,
  nameLineHeight: 1.25,
};

export const MIN_FONT_SIZE = 1;
export const DEFAULT_FONT_SIZE = 20;
export const STICKY_NOTE_MIN_FONT_SIZE = 16;
export const STICKY_NOTE_MAX_FONT_SIZE = 512;
export const STICKY_NOTE_FALLBACK_FONT_SIZE = 28;
export const STICKY_NOTE_FONT_STEP = 2;
export const STICKY_NOTE_PADDING = 16;
/**
 * The creation-date footer: a 20px text row under the label body plus a 12px
 * gap above it, inside the note's bottom padding. Reserved for every note —
 * also when `created` is unknown — so geometry never depends on data
 * availability. The label is chosen by width bucket, never measured, so
 * painting stays measurement-free (the server has no text measurer):
 * `minBodyWidthForYear` is the worst case ("30 May 2026") in the system sans
 * stack at 12px, with margin for wider fallbacks such as DejaVu Sans.
 */
/**
 * The creation-date footer of a sticky note. The label body ends `height`
 * above the note's bottom padding, and the date's baseline sits
 * `baselineFromBottom` above the note's bottom edge — so the 12px glyphs
 * (~9px above the baseline, ~3px below) end up roughly 11px from the edge
 * with a ~13px gap to the label body above them.
 */
export const STICKY_NOTE_FOOTER = {
  height: 20,
  fontSize: 12,
  fontFamily: 'Helvetica, Arial, sans-serif',
  baselineFromBottom: 14,
  opacity: 1,
  minBodyWidthForYear: 80,
} as const;
/**
 * outer height → label body height: top + bottom padding + footer. The body
 * is what the label is fitted into; a middle-aligned label is still centered
 * in the whole padded note when it fits (see `computeBoundTextPosition`).
 */
export const STICKY_NOTE_BODY_INSET_Y =
  STICKY_NOTE_PADDING * 2 + STICKY_NOTE_FOOTER.height;
export const DEFAULT_STICKY_NOTE_SIZE = 250;
// floor for a finalized note's width and base height; the UI floor is font-aware
// on top of it (see `getStickyNoteMinSize`)
export const STICKY_NOTE_MIN_SIZE = 75;
export const STICKY_NOTE_SHADOW_OFFSET = 3;
export const STICKY_NOTE_SHADOW_OPACITY = 0.16;
export const STICKY_NOTE_EDGE_SHADOW_WIDTH = 0.5;
export const STICKY_NOTE_EDGE_SHADOW_OPACITY = 0.08;
export const DEFAULT_FONT_FAMILY: FontFamilyValues = FONT_FAMILY.Sans;
export const DEFAULT_TEXT_ALIGN = 'left';
export const DEFAULT_VERTICAL_ALIGN = 'top';
export const DEFAULT_VERSION = '{version}';
export const DEFAULT_TRANSFORM_HANDLE_SPACING = 2;

export const SIDE_RESIZING_THRESHOLD = 2 * DEFAULT_TRANSFORM_HANDLE_SPACING;
// a small epsilon to make side resizing always take precedence
// (avoids an increase in renders and changes to tests)
export const EPSILON = 0.00001;
export const DEFAULT_COLLISION_THRESHOLD =
  2 * SIDE_RESIZING_THRESHOLD - EPSILON;

export const COLOR_WHITE = '#ffffff';
export const COLOR_CHARCOAL_BLACK = '#1e1e1e';
// keep this in sync with CSS
export const COLOR_VOICE_CALL = '#a2f1a6';

export const DEFAULT_GRID_SIZE = 20;
export const DEFAULT_GRID_STEP = 5;

export const IMAGE_MIME_TYPES = {
  svg: 'image/svg+xml',
  png: 'image/png',
  jpg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  bmp: 'image/bmp',
  ico: 'image/x-icon',
  avif: 'image/avif',
  jfif: 'image/jfif',
} as const;

export const STRING_MIME_TYPES = {
  text: 'text/plain',
  html: 'text/html',
  json: 'application/json',
  // excalidraw data
  excalidraw: 'application/vnd.excalidraw+json',
  excalidrawClipboard: 'application/vnd.excalidraw.clipboard+json',
  // LEGACY: fully-qualified library JSON data
  excalidrawlib: 'application/vnd.excalidrawlib+json',
  // list of excalidraw library item ids
  excalidrawlibIds: 'application/vnd.excalidrawlib.ids+json',
} as const;

export const MIME_TYPES = {
  ...STRING_MIME_TYPES,
  // image-encoded excalidraw data
  'excalidraw.svg': 'image/svg+xml',
  'excalidraw.png': 'image/png',
  // binary
  binary: 'application/octet-stream',
  // image
  ...IMAGE_MIME_TYPES,
} as const;

export const ALLOWED_PASTE_MIME_TYPES = [
  MIME_TYPES.text,
  MIME_TYPES.html,
  ...Object.values(IMAGE_MIME_TYPES),
] as const;

export const EXPORT_IMAGE_TYPES = {
  png: 'png',
  svg: 'svg',
  clipboard: 'clipboard',
} as const;

export const EXPORT_DATA_TYPES = {
  excalidraw: 'excalidraw',
  excalidrawClipboard: 'excalidraw/clipboard',
  excalidrawLibrary: 'excalidrawlib',
  excalidrawClipboardWithAPI: 'excalidraw-api/clipboard',
} as const;

// time in milliseconds
export const IMAGE_RENDER_TIMEOUT = 500;
export const TAP_TWICE_TIMEOUT = 300;
export const TOUCH_CTX_MENU_TIMEOUT = 500;
export const SCROLL_TIMEOUT = 100;
export const ZEN_MODE_TRANSITION_DURATION = 250;
export const ZOOM_STEP = 0.1;
export const MIN_ZOOM = 0.1;
export const MAX_ZOOM = 30;
/** 100% zoom, for computations that have no editor zoom to go by */
export const DEFAULT_ZOOM: AppState['zoom'] = Object.freeze({
  value: 1 as NormalizedZoomValue,
});
export const HYPERLINK_TOOLTIP_DELAY = 300;

// Report a user inactive after IDLE_THRESHOLD milliseconds
export const IDLE_THRESHOLD = 60_000;
// Report a user active each ACTIVE_THRESHOLD milliseconds
export const ACTIVE_THRESHOLD = 3_000;

export const MAX_DECIMALS_FOR_SVG_EXPORT = 2;

export const EXPORT_SCALES = [1, 2, 3];
export const DEFAULT_EXPORT_PADDING = 10; // px

export const DEFAULT_IMAGE_OPTIONS: AppProps['imageOptions'] = {
  maxWidthOrHeight: 1440,
  maxFileSizeBytes: 4 * 1024 * 1024,
};

export const SVG_NS = 'http://www.w3.org/2000/svg';

export const VERSIONS = {
  excalidraw: 2,
  excalidrawLibrary: 2,
} as const;

export const BOUND_TEXT_PADDING = 5;
export const ARROW_LABEL_WIDTH_FRACTION = 0.7;
export const ARROW_LABEL_FONT_SIZE_TO_MIN_WIDTH_RATIO = 11;

export const VERTICAL_ALIGN = {
  TOP: 'top',
  MIDDLE: 'middle',
  BOTTOM: 'bottom',
};

export const TEXT_ALIGN = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
};

export const ELEMENT_READY_TO_ERASE_OPACITY = 20;
export const ELEMENT_PENDING_DRAW_SHAPE_OPACITY = 70;

// Radius represented as 25% of element's largest side (width/height).
// Used for LEGACY and PROPORTIONAL_RADIUS algorithms, or when the element is
// below the cutoff size.
export const DEFAULT_PROPORTIONAL_RADIUS = 0.25;
// Fixed radius for the ADAPTIVE_RADIUS algorithm. In pixels.
export const DEFAULT_ADAPTIVE_RADIUS = 32;
// roundness type (algorithm)
export const ROUNDNESS = {
  // Used for legacy rounding (rectangles), which currently works the same
  // as PROPORTIONAL_RADIUS, but we need to differentiate for UI purposes and
  // forwards-compat.
  LEGACY: 1,

  // Used for linear elements & diamonds
  PROPORTIONAL_RADIUS: 2,

  // Current default algorithm for rectangles, using fixed pixel radius.
  // It's working similarly to a regular border-radius, but attemps to make
  // radius visually similar across differnt element sizes, especially
  // very large and very small elements.
  //
  // NOTE right now we don't allow configuration and use a constant radius
  // (see DEFAULT_ADAPTIVE_RADIUS constant)
  ADAPTIVE_RADIUS: 3,
  // Explicit document-space radius, independent of element dimensions.
  FIXED_RADIUS: 4,
} as const;

export const ROUGHNESS = {
  architect: 0,
  artist: 1,
  cartoonist: 2,
} as const;

export type StrokeWidthKey = 'thin' | 'medium' | 'bold';

export const STROKE_WIDTH_KEYS: readonly StrokeWidthKey[] = [
  'thin',
  'medium',
  'bold',
];

export const STROKE_WIDTH: Readonly<
  Record<StrokeWidthKey | 'extraBold', ExcalidrawElement['strokeWidth']>
> = {
  thin: 1,
  medium: 2,
  bold: 4,
  extraBold: 8, // unused (may be introduced in the future)
};

// freedraw schema 2.0 uses thinner stroke, but to maintain backwards and
// forwards compatibility, instead of changing the shape renderer, we scale
// the stroke width by 1/2 (previous, thin was 1, medium 2 etc.)
//
// note that in the UI, STROKE_WIDTH.thin == FREEDRAW_STROKE_WIDTH.thin still
export const FREEDRAW_STROKE_WIDTH: Readonly<
  Record<StrokeWidthKey | 'extraBold', ExcalidrawElement['strokeWidth']>
> = {
  thin: 0.5,
  medium: 1,
  bold: 2,
  extraBold: 4, // legacy (may be used again in the future)
};

export const getStrokeWidthByKey = (
  elementType: ExcalidrawElement['type'],
  strokeWidthKey: StrokeWidthKey
): ExcalidrawElement['strokeWidth'] => {
  return elementType === 'freedraw'
    ? FREEDRAW_STROKE_WIDTH[strokeWidthKey]
    : STROKE_WIDTH[strokeWidthKey];
};

export const DEFAULT_ELEMENT_STROKE_WIDTH_KEY: StrokeWidthKey = 'medium';

export const DEFAULT_ELEMENT_PROPS: {
  strokeColor: ExcalidrawElement['strokeColor'];
  backgroundColor: ExcalidrawElement['backgroundColor'];
  fillStyle: ExcalidrawElement['fillStyle'];
  strokeWidth: ExcalidrawElement['strokeWidth'];
  strokeStyle: ExcalidrawElement['strokeStyle'];
  roughness: ExcalidrawElement['roughness'];
  opacity: ExcalidrawElement['opacity'];
  locked: ExcalidrawElement['locked'];
} = {
  strokeColor: COLOR_PALETTE.black,
  backgroundColor: COLOR_PALETTE.transparent,
  fillStyle: 'solid',
  strokeWidth: STROKE_WIDTH[DEFAULT_ELEMENT_STROKE_WIDTH_KEY],
  strokeStyle: 'solid',
  roughness: ROUGHNESS.architect,
  opacity: 100,
  locked: false,
};

export const LIBRARY_SIDEBAR_TAB = 'library';
export const CANVAS_SEARCH_TAB = 'search';

export const DEFAULT_SIDEBAR = {
  name: 'default',
  defaultTab: LIBRARY_SIDEBAR_TAB,
} as const;

export const LIBRARY_DISABLED_TYPES = new Set([
  'iframe',
  'embeddable',
  'image',
] as const);

// use these constants to easily identify reference sites
export const TOOL_TYPE = {
  selection: 'selection',
  lasso: 'lasso',
  rectangle: 'rectangle',
  diamond: 'diamond',
  ellipse: 'ellipse',
  arrow: 'arrow',
  line: 'line',
  freedraw: 'freedraw',
  text: 'text',
  image: 'image',
  eraser: 'eraser',
  hand: 'hand',
  frame: 'frame',
  magicframe: 'magicframe',
  stickynote: 'stickynote',
  embeddable: 'embeddable',
  laser: 'laser',
  autoshape: 'autoshape',
  bucketfill: 'bucketfill',
} as const;

export const STATS_PANELS = { generalStats: 1, elementProperties: 2 } as const;

export const MIN_WIDTH_OR_HEIGHT = 1;

export const ARROW_TYPE: { [T in AppState['currentItemArrowType']]: T } = {
  sharp: 'sharp',
  round: 'round',
  elbow: 'elbow',
};

export const DEFAULT_REDUCED_GLOBAL_ALPHA = 0.3;
export const ELEMENT_LINK_KEY = 'element';

/** used in tests */
export const ORIG_ID = Symbol.for('__test__originalId__');

export enum UserIdleState {
  ACTIVE = 'active',
  AWAY = 'away',
  IDLE = 'idle',
}

/**
 * distance at which we merge points instead of adding a new merge-point
 * when converting a line to a polygon (merge currently means overlaping
 * the start and end points)
 */
export const LINE_POLYGON_POINT_MERGE_DISTANCE = 20;

export const DOUBLE_TAP_POSITION_THRESHOLD = 35;

export const BIND_MODE_TIMEOUT = 700; // ms

// glass background for mobile action buttons
export const MOBILE_ACTION_BUTTON_BG = {
  background: 'var(--mobile-action-button-bg)',
} as const;

export const DEFAULT_STROKE_STREAMLINE = 0.5;
export const DEFAULT_STROKE_STREAMLINE_PRECISE = 0.2;
